import { Badge } from '@ophelos/ui/components/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ophelos/ui/components/collapsible';
import { ChevronDown } from 'lucide-react';
import { Fragment } from 'react';
import { Trans } from 'react-i18next';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { fromSubunits } from '@/lib/currency';
import type { PlanInstalment } from '@/lib/payment-schedule';

interface PlanScheduleListProps {
  instalments: PlanInstalment[];
  currency: string;
  /** Sum the schedule was built from, named in the header. */
  totalAmount: number;
  /** Labels the first row "Today" instead of its date. */
  startsToday: boolean;
}

/** Every instalment of a plan with its running balance, open by default under a one-line header. */
export function PlanScheduleList({ instalments, currency, totalAmount, startsToday }: PlanScheduleListProps) {
  const { t, currentLanguage } = useTranslation();
  if (instalments.length === 0) return null;
  const first = instalments[0].date;
  const last = instalments[instalments.length - 1].date;
  // Rows carry the year only once the plan is long enough for two of them to share a month and
  // day, which takes a full 12 months between the first and last instalment.
  const monthsSpanned = (last.getFullYear() - first.getFullYear()) * 12 + (last.getMonth() - first.getMonth());
  const shortFmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', ...(monthsSpanned >= 12 && { year: 'numeric' }) };
  const clearedLabel = last.toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Collapsible defaultOpen>
      <div className="bg-card border rounded-lg overflow-hidden">
        <CollapsibleTrigger asChild>
          <button type="button" className="group w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer">
            <span className="min-w-0 text-sm text-muted-foreground">
              <Trans
                i18nKey="paymentPlan.review.scheduleTitle"
                components={{ amount: <CurrencyDisplay amount={totalAmount} currency={currency} className="text-sm inline-flex gap-0" /> }}
              />
            </span>
            <span className="ml-auto shrink-0 whitespace-nowrap text-base font-bold">{clearedLabel}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/* Single grid so date / amount / balance share column tracks and line up. Column
              spacing via cell padding (not grid gap) keeps the row dividers continuous. */}
          <div className="border-t border-border py-2 grid grid-cols-[auto_1fr_auto] px-5 bg-background">
            {instalments.map((inst, i) => {
              const isLast = i === instalments.length - 1;
              const cell = `flex items-center py-2.5 `;
              return (
                <Fragment key={inst.date.toISOString()}>
                  <span className={`${cell} pr-3`}>
                    <span
                      className={`flex items-center justify-center size-6 shrink-0 rounded-full text-[0.7rem] font-bold ${
                        isLast ? 'highlight-green' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {i + 1}
                    </span>
                  </span>
                  <span className={`${cell} flex-wrap gap-x-2 gap-y-1 pr-3 text-sm text-muted-foreground min-w-0`}>
                    {i === 0 && startsToday ? t('paymentPlan.today') : inst.date.toLocaleDateString(currentLanguage, shortFmt)}
                    {isLast && (
                      <Badge variant="success" className="text-[0.6rem]">
                        {t('paymentPlan.review.clearedPill')}
                      </Badge>
                    )}
                  </span>
                  {/* Amount and running balance share a cell and stack on phones. An auto grid track
                      sizes to its content, so a wrapping row would squeeze the date column instead. */}
                  <span className={`${cell} max-xs:flex-col max-xs:items-end xs:items-baseline xs:gap-x-4 tabular-nums`}>
                    <CurrencyDisplay amount={fromSubunits(inst.subunits)} currency={currency} className="text-sm font-medium" />
                    <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                      →<CurrencyDisplay amount={fromSubunits(inst.balanceAfterSubunits)} currency={currency} className="text-xs" />
                    </span>
                  </span>
                </Fragment>
              );
            })}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
